import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import type { ThemeMode as ServerThemeMode } from "@/shared/api/types";
import { t, useLocale } from "@/shared/lib/i18n";

/**
 * Build-safe visual theme runtime.
 *
 * Local theme storage can use all client-side palettes. Account preferences and
 * REST contract are still limited to the original canonical ten, so local-only
 * themes persist on-device and are still fully usable in the interface.
 */

const SERVER_THEME_MODES = [
  "modern-light",
  "saas-navy",
  "oled-black",
  "nordic-cold",
  "solarized-light",
  "monokai",
  "high-contrast",
  "pastel-minimalist",
  "sepia-focus",
  "cyberpunk",
] as const;

export const THEME_OPTIONS = [
  {
    id: "modern-light",
    get label() {
      return t("copy.1bae74831f56");
    },
    get description() {
      return t("copy.735c05e384d9");
    },
    colorScheme: "light",
  },
  {
    id: "saas-navy",
    get label() {
      return t("copy.66e1c11b287a");
    },
    get description() {
      return t("copy.c5d975e18199");
    },
    colorScheme: "dark",
  },
  {
    id: "oled-black",
    get label() {
      return t("copy.3af07dceb19d");
    },
    get description() {
      return t("copy.88dbcb2b0903");
    },
    colorScheme: "dark",
  },
  {
    id: "nordic-cold",
    get label() {
      return t("copy.87477d444bd6");
    },
    get description() {
      return t("copy.8de5e0a8a700");
    },
    colorScheme: "light",
  },
  {
    id: "solarized-light",
    get label() {
      return t("copy.320515b8209b");
    },
    get description() {
      return t("copy.a4830d3dd2fd");
    },
    colorScheme: "light",
  },
  {
    id: "monokai",
    get label() {
      return t("copy.af30a45d7674");
    },
    get description() {
      return t("copy.b195b5c41545");
    },
    colorScheme: "dark",
  },
  {
    id: "high-contrast",
    get label() {
      return t("copy.faf73cec0083");
    },
    get description() {
      return t("copy.331e054eab13");
    },
    colorScheme: "dark",
  },
  {
    id: "pastel-minimalist",
    get label() {
      return t("copy.a565583a03f2");
    },
    get description() {
      return t("copy.13b7ae63ab89");
    },
    colorScheme: "light",
  },
  {
    id: "sepia-focus",
    get label() {
      return t("copy.12d74372034a");
    },
    get description() {
      return t("copy.42e50ee6cccf");
    },
    colorScheme: "light",
  },
  {
    id: "cyberpunk",
    get label() {
      return t("copy.84ad688ed1e8");
    },
    get description() {
      return t("copy.76e9049df535");
    },
    colorScheme: "dark",
  },
  {
    id: "mint-breeze",
    get label() {
      return t("theme.mint_breeze.label");
    },
    get description() {
      return t("theme.mint_breeze.description");
    },
    colorScheme: "light",
  },
  {
    id: "paper-glow",
    get label() {
      return t("theme.paper_glow.label");
    },
    get description() {
      return t("theme.paper_glow.description");
    },
    colorScheme: "light",
  },
  {
    id: "sandstone-rose",
    get label() {
      return t("theme.sandstone_rose.label");
    },
    get description() {
      return t("theme.sandstone_rose.description");
    },
    colorScheme: "light",
  },
  {
    id: "sunset-amber",
    get label() {
      return t("theme.sunset_amber.label");
    },
    get description() {
      return t("theme.sunset_amber.description");
    },
    colorScheme: "light",
  },
  {
    id: "lavender-veil",
    get label() {
      return t("theme.lavender_veil.label");
    },
    get description() {
      return t("theme.lavender_veil.description");
    },
    colorScheme: "dark",
  },
  {
    id: "ocean-mist",
    get label() {
      return t("theme.ocean_mist.label");
    },
    get description() {
      return t("theme.ocean_mist.description");
    },
    colorScheme: "dark",
  },
  {
    id: "forest-night",
    get label() {
      return t("theme.forest_night.label");
    },
    get description() {
      return t("theme.forest_night.description");
    },
    colorScheme: "dark",
  },
  {
    id: "midnight-cobalt",
    get label() {
      return t("theme.midnight_cobalt.label");
    },
    get description() {
      return t("theme.midnight_cobalt.description");
    },
    colorScheme: "dark",
  },
  {
    id: "copper-rust",
    get label() {
      return t("theme.copper_rust.label");
    },
    get description() {
      return t("theme.copper_rust.description");
    },
    colorScheme: "dark",
  },
] as const;

export type ThemeId = (typeof THEME_OPTIONS)[number]["id"];

type ThemeContextValue = {
  mode: ThemeId;
  resolved: ThemeId;
  setMode: (mode: ThemeId) => void;
  syncAccountTheme: (mode: ServerThemeMode) => void;
};

const STORAGE_KEY = "exognitos.theme.v3";
const PREVIOUS_STORAGE_KEY = "exognitos.theme.v2";
const LEGACY_STORAGE_KEY = "exognitos.theme";
const ThemeContext = createContext<ThemeContextValue | null>(null);

export const DEFAULT_THEME: ThemeId = "saas-navy";

export const THEME_CHROME_COLORS: Record<ThemeId, string> = {
  "modern-light": "#f7f7f5",
  "saas-navy": "#12161f",
  "oled-black": "#070708",
  "nordic-cold": "#eef4fa",
  "solarized-light": "#fdf6e3",
  monokai: "#272822",
  "high-contrast": "#000000",
  "pastel-minimalist": "#fdf7fb",
  "sepia-focus": "#f6efdf",
  cyberpunk: "#120b23",
  "mint-breeze": "#edf9f0",
  "paper-glow": "#fef9f0",
  "sandstone-rose": "#f8ece2",
  "sunset-amber": "#f8f0e4",
  "lavender-veil": "#201734",
  "ocean-mist": "#16233a",
  "forest-night": "#15211a",
  "midnight-cobalt": "#151a2d",
  "copper-rust": "#26170e",
};

export function isServerThemeMode(value: ThemeId): value is ServerThemeMode {
  return (SERVER_THEME_MODES as readonly string[]).includes(value);
}

function isThemeId(value: string | null): value is ThemeId {
  return THEME_OPTIONS.some((theme) => theme.id === value);
}
function themeOption(mode: ThemeId) {
  return THEME_OPTIONS.find((theme) => theme.id === mode) ?? THEME_OPTIONS[1];
}

export function themeForServerMode(mode: ServerThemeMode): ThemeId {
  return mode;
}

export function isLightTheme(mode: ThemeId): boolean {
  return themeOption(mode).colorScheme === "light";
}

function applyTheme(mode: ThemeId): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = mode;
  document.documentElement.style.colorScheme = isLightTheme(mode) ? "light" : "dark";
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_CHROME_COLORS[mode]);
}

function persistStored(mode: ThemeId): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
    window.localStorage.removeItem(PREVIOUS_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // A browser policy may block storage. Keep the in-memory theme usable.
  }
}

function migrateStored(value: string | null): ThemeId | null {
  if (isThemeId(value)) return value;
  if (value === "light") return "modern-light";
  if (value === "navy") return "saas-navy";
  if (value === "dark") return "oled-black";
  return null;
}

function readStored(): ThemeId {
  if (typeof window === "undefined") return DEFAULT_THEME;

  try {
    const stored = migrateStored(window.localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;

    const previous = migrateStored(window.localStorage.getItem(PREVIOUS_STORAGE_KEY));
    const legacy = migrateStored(window.localStorage.getItem(LEGACY_STORAGE_KEY));
    const migrated = previous ?? legacy;
    if (migrated) return migrated;
  } catch {
    // Storage can be unavailable by policy. The default remains usable.
  }

  return DEFAULT_THEME;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  useLocale();
  const [mode, setModeState] = useState<ThemeId>(readStored);
  const resolved = mode;

  useLayoutEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setMode = useCallback((next: ThemeId) => {
    applyTheme(next);
    setModeState(next);
    persistStored(next);
  }, []);

  const syncAccountTheme = useCallback((accountMode: ServerThemeMode) => {
    const next = themeForServerMode(accountMode);
    applyTheme(next);
    setModeState(next);
    persistStored(next);
  }, []);

  const value = useMemo(
    () => ({ mode, resolved, setMode, syncAccountTheme }),
    [mode, resolved, setMode, syncAccountTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme, ThemeProvider içinde kullanılmalı.");
  return context;
}

/** True for themes that render native controls with a dark color scheme. */
export function useIsDark(): boolean {
  return !isLightTheme(useTheme().resolved);
}

/** Respect the operating-system setting for people who asked for less motion. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const listener = (event: MediaQueryListEvent) => setReduced(event.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, []);
  return reduced;
}
